import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaHistory } from "react-icons/fa";

import {
    get_local_server_update_histories,
    get_local_server_update_history,
    get_local_server_port_histories
} from "../../lib/api/local";
import WebPagination from "../etc/WebPagination";

// 20260814 업데이트 상태를 완료·진행·대기·주의·실패로 구분해 표시한다.
const getUpdateStatusStyle = (status) => {
    switch (String(status || "").toUpperCase()) {
        case "SUCCESS": return "success";
        case "STARTED":
        case "RUNNING": return "progress";
        case "REBOOT_PENDING": return "pending";
        case "PARTIAL": return "warning";
        case "FAILED": return "failed";
        default: return "unknown";
    }
};

// 20260814 진행 단계도 완료·재부팅 대기·진행·오류 상태로 구분한다.
const getUpdateStageStyle = (stage) => {
    const normalizedStage = String(stage || "").toUpperCase();
    if (normalizedStage === "COMPLETED") return "success";
    if (normalizedStage === "BOOTSTRAP_REBOOT_PENDING" || normalizedStage === "APPLY_REBOOT_PENDING") return "pending";
    if (normalizedStage.includes("FAIL") || normalizedStage.includes("ERROR")) return "failed";
    if (["COLLECTING", "DOWNFILES_UPDATING", "APPLYING", "DB_MIGRATING", "VERIFYING"].includes(normalizedStage)) return "progress";
    return "unknown";
};

const WebLocalServerHistory = ({ localServerSeq, localServerName, localServerInfo, handleBack }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("UPDATE");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [blockNum, setBlockNum] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [listHistory, setListHistory] = useState([]);
    const [selectedRunId, setSelectedRunId] = useState(null);
    const [listComponentHistory, setListComponentHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // 20260814 일반 상태값의 T는 보존하고 ISO 날짜의 날짜·시간 구분자만 공백으로 변경한다.
    const formatOrDash = (value) => value === undefined || value === null || value === "" ? "-" : String(value).replace(/(\d{4}-\d{2}-\d{2})T(?=\d{2}:)/, "$1 ").replace(".000Z", "");

    // 20260814 SVN 작업 복사본 상태와 원인 코드를 관리자가 이해할 수 있는 문구로 변환한다.
    const getWorkingCopyStatus = (status) => {
        const normalizedStatus = String(status || "").toUpperCase();
        if (normalizedStatus === "NORMAL") return { label: t("작업복사본_정상", { ns: "admin" }), style: "success" };
        if (normalizedStatus === "UNKNOWN") return { label: t("작업복사본_확인불가", { ns: "admin" }), style: "failed" };
        return { label: formatOrDash(status), style: "unknown" };
    };

    const getComponentReasons = (component) => {
        const reasons = [];
        if (component.LOCAL_MODIFIED_YN === "Y") reasons.push({ key: "local-modified", label: t("원인_로컬수정", { ns: "admin" }), style: "warning", title: "LOCAL_MODIFIED_YN" });
        if (component.MIXED_REVISION_YN === "Y") reasons.push({ key: "mixed-revision", label: t("원인_혼합리비전", { ns: "admin" }), style: "warning", title: "MIXED_REVISION_YN" });
        if (component.SWITCHED_YN === "Y") reasons.push({ key: "switched", label: t("원인_전환경로", { ns: "admin" }), style: "warning", title: "SWITCHED_YN" });
        if (component.ERROR_MESSAGE) {
            const errorLabel = component.ERROR_MESSAGE === "SVN_WORKING_COPY_UNAVAILABLE"
                ? t("원인_SVN작업복사본확인불가", { ns: "admin" })
                : component.ERROR_MESSAGE;
            reasons.push({ key: `error-${component.ERROR_MESSAGE}`, label: errorLabel, style: "failed", title: component.ERROR_MESSAGE });
        }
        return reasons;
    };

    useEffect(() => {
        let isMounted = true;
        const loadHistories = async () => {
            setIsLoading(true);
            setErrorMessage("");
            setSelectedRunId(null);
            setListComponentHistory([]);
            try {
                const offset = (page - 1) * limit;
                const response = activeTab === "UPDATE"
                    ? await get_local_server_update_histories({ localServerSeq, limit, offset })
                    : await get_local_server_port_histories({ localServerSeq, limit, offset });
                if (!isMounted) return;
                if (response.data && response.data.isSuccessed) {
                    setTotalCount(Number(response.data.totalCount || 0));
                    setListHistory(activeTab === "UPDATE" ? (response.data.listUpdateHistory || []) : (response.data.listPortHistory || []));
                } else {
                    setTotalCount(0);
                    setListHistory([]);
                    setErrorMessage(t("로컬_서버_이력_조회_실패", { ns: "admin" }));
                }
            } catch (err) {
                if (!isMounted) return;
                setTotalCount(0);
                setListHistory([]);
                setErrorMessage(t("로컬_서버_이력_조회_실패", { ns: "admin" }));
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        loadHistories();
        return () => { isMounted = false; };
    }, [activeTab, page, limit, localServerSeq, t]);

    const changeTab = (tab) => {
        setActiveTab(tab);
        setPage(1);
        setBlockNum(0);
    };

    const toggleUpdateDetail = async (updateRunId) => {
        if (selectedRunId === updateRunId) {
            setSelectedRunId(null);
            setListComponentHistory([]);
            return;
        }
        try {
            const response = await get_local_server_update_history({ localServerSeq, updateRunId });
            if (response.data && response.data.isSuccessed) {
                setSelectedRunId(updateRunId);
                setListComponentHistory(response.data.listComponentHistory || []);
            } else {
                setErrorMessage(t("로컬_서버_이력_상세_조회_실패", { ns: "admin" }));
            }
        } catch (err) {
            setErrorMessage(t("로컬_서버_이력_상세_조회_실패", { ns: "admin" }));
        }
    };

    return (
        <div className="web-main admin list-area">
            <div className="app-container">
                <div className="main-title-bar">
                    <div className="title-left">
                        <FaHistory className="icon-teacher" aria-hidden="true" />
                        <span className="title-text">{t("로컬_서버_이력", { ns: "admin" })}</span>
                    </div>
                </div>

                {/* 20260814 목록 이동 버튼을 제목과 이력 탭 사이의 독립된 행에 배치한다. */}
                <div className="local-history-back-row">
                    <button type="button" className="local-history-back-button" onClick={handleBack}>
                        <FaArrowLeft aria-hidden="true" />
                        <span>{t("목록으로", { ns: "admin" })}</span>
                    </button>
                </div>

                {/* 20260814 선생님·클래스 관리 화면과 동일한 탭 디자인을 사용한다. */}
                <div className="page-nav-tabs local-history-tabs">
                    <button type="button" className={`nav-tab${activeTab === "UPDATE" ? " active" : ""}`} onClick={() => changeTab("UPDATE")}>{t("업데이트_이력", { ns: "admin" })}</button>
                    <button type="button" className={`nav-tab${activeTab === "PORT" ? " active" : ""}`} onClick={() => changeTab("PORT")}>{t("포트_이력", { ns: "admin" })}</button>
                </div>

                {errorMessage && <div className="alert alert-danger text-center mx-2">{errorMessage}</div>}
                {/* 20260814 페이지 이동 영역을 탭과 이력 목록 사이에 배치한다. */}
                <WebPagination total={totalCount <= 0 ? 1 : totalCount} limit={limit} setLimit={setLimit} page={page} setPage={setPage} blockNum={blockNum} setBlockNum={setBlockNum} style={{ marginTop: '14px', marginBottom: '24px' }} />

                {/* 20260814 탭과 페이지 선택 후 현재 로컬 서버의 주요 상태를 확인할 수 있게 한다. */}
                <div className="local-history-server-summary">
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("로컬_서버_순번", { ns: "admin" })}</span>
                        <span className="local-history-server-seq">#{localServerSeq}</span>
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("로컬_서버_이름", { ns: "admin" })}</span>
                        <span className="local-history-server-name">{formatOrDash(localServerInfo ? localServerInfo.localServerName : localServerName)}</span>
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("로컬_사이트", { ns: "admin" })}</span>
                        <span className="local-history-info-value">{formatOrDash(localServerInfo && localServerInfo.localSiteName)}</span>
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("서버_접속_상태", { ns: "admin" })}</span>
                        {localServerInfo ? <span className={`local-server-state-badge server-state-${localServerInfo.serverStatus === "Y" ? "online" : "offline"}`}>{t(localServerInfo.serverStatus === "Y" ? "접속중" : "미접속", { ns: "admin" })}</span> : <span className="local-history-info-value">-</span>}
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("로컬_서버_타입", { ns: "admin" })}</span>
                        {localServerInfo ? <span className={`local-server-type-badge local-server-type-${localServerInfo.localServerType && localServerInfo.localServerType !== "UNKNOWN" ? "configured" : "unknown"}`}>{t(`서버_타입_${localServerInfo.localServerType || "UNKNOWN"}`, { ns: "admin", defaultValue: localServerInfo.localServerTypeName || "-" })}</span> : <span className="local-history-info-value">-</span>}
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("포트_상태", { ns: "admin" })}</span>
                        {localServerInfo ? <span className={`local-server-state-badge server-state-${localServerInfo.isPortOpened ? "online" : "neutral"}`}>{t(localServerInfo.isPortOpened ? "열림" : "닫힘", { ns: "admin" })}</span> : <span className="local-history-info-value">-</span>}
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("설치_SVN_리비전", { ns: "admin" })}</span>
                        <span className="local-history-info-value">{formatOrDash(localServerInfo && localServerInfo.installedSvnRevision)}</span>
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("MQTT_사용_가능", { ns: "admin" })}</span>
                        {localServerInfo ? <span className={`local-server-state-badge server-state-${localServerInfo.mqttAvailableYn === "Y" ? "online" : "neutral"}`}>{localServerInfo.mqttAvailableYn === "Y" ? "Y" : "N"}</span> : <span className="local-history-info-value">-</span>}
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("마지막_업데이트_상태", { ns: "admin" })}</span>
                        {localServerInfo && localServerInfo.lastUpdateStatus ? <span className={`local-update-status-badge update-status-${getUpdateStatusStyle(localServerInfo.lastUpdateStatus)}`}>{formatOrDash(localServerInfo.lastUpdateStatus)}</span> : <span className="local-history-info-value">-</span>}
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("마지막_업데이트_시각", { ns: "admin" })}</span>
                        <span className="local-history-info-value">{formatOrDash(localServerInfo && localServerInfo.lastUpdateDt)}</span>
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("기능_확인_시각", { ns: "admin" })}</span>
                        <span className="local-history-info-value">{formatOrDash(localServerInfo && localServerInfo.capabilityCheckDt)}</span>
                    </div>
                    {/* 20260819 이력 확인 중에도 로컬 서버의 최근 로그인·로그아웃 시각을 함께 표시한다. */}
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("마지막_로그인", { ns: "admin" })}</span>
                        <span className="local-history-info-value">{formatOrDash(localServerInfo && localServerInfo.serverLoginDt)}</span>
                    </div>
                    <div className="local-history-info-item">
                        <span className="local-history-info-label">{t("마지막_로그아웃", { ns: "admin" })}</span>
                        <span className="local-history-info-value">{formatOrDash(localServerInfo && localServerInfo.serverLogoutDt)}</span>
                    </div>
                </div>

                {isLoading ?
                    <div className="text-center grey-text p-4">{t("조회중", { ns: "admin" })}</div> :
                    activeTab === "UPDATE" ?
                        <div className="mx-2 local-update-history-list">
                            {/* 20260814 업데이트 이력은 헤더 없는 정보 카드로 구분해 표시한다. */}
                            {listHistory.length > 0 ? listHistory.map((info, index) => (
                                <div className={`local-update-history-card update-status-card-${getUpdateStatusStyle(info.UPDATE_STATUS)}`} key={`${info.UPDATE_RUN_ID || "update"}-${index}`}>
                                    <div className="local-update-history-summary">
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("실행_ID", { ns: "admin" })}</span>
                                            <span className="local-history-info-value">{formatOrDash(info.UPDATE_RUN_ID)}</span>
                                        </div>
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("업데이트_상태", { ns: "admin" })}</span>
                                            <span className={`local-update-status-badge update-status-${getUpdateStatusStyle(info.UPDATE_STATUS)}`}>{formatOrDash(info.UPDATE_STATUS)}</span>
                                        </div>
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("진행_단계", { ns: "admin" })}</span>
                                            <span className={`local-update-status-badge update-status-${getUpdateStageStyle(info.CURRENT_STAGE)}`}>{formatOrDash(info.CURRENT_STAGE)}</span>
                                        </div>
                                        <div className="local-history-info-item local-history-detail-action">
                                            <span className="local-history-info-label">{t("상세", { ns: "admin" })}</span>
                                            <button type="button" className="btn-table-action history-detail-button" onClick={() => toggleUpdateDetail(info.UPDATE_RUN_ID)}>{t("상세", { ns: "admin" })}</button>
                                        </div>
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("설치_SVN_리비전", { ns: "admin" })}</span>
                                            <span className="local-history-info-value">{formatOrDash(info.INSTALLED_SVN_REVISION)}</span>
                                        </div>
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("MQTT_포트", { ns: "admin" })}</span>
                                            {/* 20260814 MQTT 포트 상태도 현재 정보 카드와 동일한 배지로 표시한다. */}
                                            <span className={`local-server-state-badge server-state-${info.MQTT_CONNECTED_YN === "Y" ? "online" : "neutral"}`}>{info.MQTT_CONNECTED_YN === "Y" ? "Y" : "N"}</span>
                                        </div>
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("시작_시각", { ns: "admin" })}</span>
                                            <span className="local-history-info-value">{formatOrDash(info.START_DT)}</span>
                                        </div>
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("완료_시각", { ns: "admin" })}</span>
                                            <span className="local-history-info-value">{formatOrDash(info.COMPLETE_DT)}</span>
                                        </div>
                                    </div>
                                    {selectedRunId === info.UPDATE_RUN_ID && <div className="history-detail-row">
                                        <table className="table table-sm mb-0 local-history-detail-table"><thead><tr><th>{t("수집_시점", { ns: "admin" })}</th><th>{t("컴포넌트", { ns: "admin" })}</th><th>{t("SVN_리비전", { ns: "admin" })}</th><th>{t("SVN_버전", { ns: "admin" })}</th><th>{t("작업_복사본_상태", { ns: "admin" })}</th><th>{t("비고", { ns: "admin" })}</th></tr></thead>
                                            <tbody>{listComponentHistory.length > 0 ? listComponentHistory.map((component, componentIndex) => {
                                                const workingCopyStatus = getWorkingCopyStatus(component.WORKING_COPY_STATUS);
                                                const componentReasons = getComponentReasons(component);
                                                return <tr key={`${component.SNAPSHOT_TYPE}-${component.COMPONENT_TYPE}-${componentIndex}`}><td>{formatOrDash(component.SNAPSHOT_TYPE)}</td><td>{formatOrDash(component.COMPONENT_TYPE)}</td><td>{formatOrDash(component.SVN_REVISION)}</td><td>{formatOrDash(component.SVN_VERSION)}</td><td><span className={`component-status-badge component-status-${workingCopyStatus.style}`} title={component.WORKING_COPY_STATUS}>{workingCopyStatus.label}</span></td><td>{componentReasons.length > 0 ? <div className="component-reason-list">{componentReasons.map(reason => <span className={`component-reason-badge component-reason-${reason.style}`} key={reason.key} title={reason.title}>{reason.label}</span>)}</div> : "-"}</td></tr>;
                                            }) : <tr><td colSpan={6} className="grey-text">{t("이력_없음", { ns: "admin" })}</td></tr>}</tbody>
                                        </table>
                                    </div>}
                                </div>
                            )) : <div className="text-center grey-text p-4">{t("이력_없음", { ns: "admin" })}</div>}
                        </div> :
                        <div className="mx-2 local-port-history-list">
                            {/* 20260814 포트 이력도 상태와 비고를 확인하기 쉬운 카드로 표시한다. */}
                            {listHistory.length > 0 ? listHistory.map((info, index) => (
                                <div className={`local-port-history-card port-status-card-${info.OPEN_YN === "Y" ? "open" : "closed"}`} key={`${info.HS_SEQ || info.hsSeq || "port"}-${index}`}>
                                    <div className="local-port-history-summary">
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("포트_번호", { ns: "admin" })}</span>
                                            <span className="local-history-info-value">{formatOrDash(info.PORT_NUM)}</span>
                                        </div>
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("포트_상태", { ns: "admin" })}</span>
                                            <span className={`local-port-status-badge port-status-${info.OPEN_YN === "Y" ? "open" : "closed"}`}>{t(info.OPEN_YN === "Y" ? "열림" : "닫힘", { ns: "admin" })}</span>
                                        </div>
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("시작_시각", { ns: "admin" })}</span>
                                            <span className="local-history-info-value">{formatOrDash(info.STRT_DT)}</span>
                                        </div>
                                        <div className="local-history-info-item">
                                            <span className="local-history-info-label">{t("종료_시각", { ns: "admin" })}</span>
                                            <span className="local-history-info-value">{formatOrDash(info.END_DT)}</span>
                                        </div>
                                        <div className="local-history-info-item local-port-history-remarks">
                                            <span className="local-history-info-label">{t("비고", { ns: "admin" })}</span>
                                            <span className="local-history-info-value">{formatOrDash(info.OPEN_REASON)}</span>
                                        </div>
                                    </div>
                                </div>
                            )) : <div className="text-center grey-text p-4">{t("이력_없음", { ns: "admin" })}</div>}
                        </div>
                }
            </div>
        </div>
    );
};

export default WebLocalServerHistory;
